'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const coreFeatures = [
  {
    num: '01',
    icon: 'MicrophoneIcon',
    label: 'AI Voice Bot for Inbound & Outbound Calls',
    desc: 'Intelligent voice bots handle incoming customer queries, booking requests, and complaints — and proactively make outbound calls for confirmations, reminders, and follow-ups, without any human intervention.',
    pills: ['Inbound handling', 'Outbound campaigns', 'Natural language', 'Context memory'],
  },
  {
    num: '02',
    icon: 'ChatBubbleLeftRightIcon',
    label: 'Multilingual Support — Tamil, Hindi, English & More',
    desc: 'Serve customers in their preferred language. The AI voice engine supports Tamil, Hindi, English, Kannada, and Telugu — switching mid-conversation based on caller preference automatically.',
    pills: ['Tamil', 'Hindi', 'English', 'Auto language detect'],
  },
  {
    num: '03',
    icon: 'BoltIcon',
    label: 'Instant Booking Confirmations via Call',
    desc: 'Every new booking triggers an automated confirmation call to both customer and driver — with order ID, pickup details, ETA, and driver contact — reducing no-shows and confusion.',
    pills: ['Customer call', 'Driver call', 'ETA sharing', 'Order details'],
  },
  {
    num: '04',
    icon: 'ClockIcon',
    label: '24/7 Automated Customer Support',
    desc: 'Your support line never sleeps. The AI handles FAQs, order status queries, cancellation requests, and complaint logging around the clock — escalating only the cases that truly need a human.',
    pills: ['FAQ handling', 'Order status', 'Cancellations', 'Auto-escalation'],
  },
  {
    num: '05',
    icon: 'ChartBarIcon',
    label: 'Call Analytics & Sentiment Tracking',
    desc: 'Every call is transcribed, tagged, and analysed. Track resolution rates, call durations, customer sentiment scores, and common complaint patterns from a real-time analytics dashboard.',
    pills: ['Transcription', 'Sentiment score', 'Resolution rate', 'Trend analysis'],
  },
  {
    num: '06',
    icon: 'ArrowPathIcon',
    label: 'CRM Integration & Escalation Routing',
    desc: 'Seamlessly integrates with your operator CRM. Customer call history, issue tags, and unresolved tickets are automatically synced — and complex cases are routed to the right human agent instantly.',
    pills: ['CRM sync', 'Ticket creation', 'Smart routing', 'Agent handoff'],
  },
];

const useCases = [
  {
    icon: 'TruckIcon',
    title: 'Ride Hailing',
    items: ['Booking confirmation calls', 'Driver arrival alerts', 'Trip cancellation handling', 'Lost & found support'],
  },
  {
    icon: 'ArrowPathIcon',
    title: 'Logistics',
    items: ['Delivery ETAs to customers', 'Warehouse pickup confirmation', 'POD follow-up calls', 'Return & refusal handling'],
  },
  {
    icon: 'BoltIcon',
    title: 'Courier Delivery',
    items: ['Order dispatch notification', 'Address confirmation calls', 'Delivery attempt follow-up', 'COD collection reminders'],
  },
  {
    icon: 'ShoppingBagIcon',
    title: 'E-Commerce',
    items: ['Order placed confirmation', 'Out-for-delivery alerts', 'Return request processing', 'Payment collection calls'],
  },
];

const stats = [
  { value: '24/7', label: 'Always-On Support', icon: 'ClockIcon' },
  { value: '5+', label: 'Languages Supported', icon: 'GlobeAltIcon' },
  { value: '< 2s', label: 'Average Response Time', icon: 'BoltIcon' },
  { value: '80%', label: 'Query Auto-Resolution Rate', icon: 'CheckCircleIcon' },
];

const workflowSteps = [
  { icon: 'PhoneArrowDownLeftIcon', title: 'Call Received', desc: 'Customer calls your branded number. AI greets them instantly.' },
  { icon: 'MicrophoneIcon', title: 'Intent Identified', desc: 'NLP engine identifies booking, complaint, status, or query.' },
  { icon: 'BoltIcon', title: 'Action Taken', desc: 'AI resolves instantly — confirms booking, shares status, logs complaint.' },
  { icon: 'ArrowPathIcon', title: 'CRM Updated', desc: 'Interaction is logged, transcribed, and synced to your CRM.' },
  { icon: 'UserIcon', title: 'Escalation (if needed)', desc: 'Complex cases handed to a human agent with full context.' },
  { icon: 'ChartBarIcon', title: 'Analytics Dashboard', desc: 'Every call feeds real-time reports on resolution and sentiment.' },
];

export default function AICallCenterContent() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (el) {
      const t = setTimeout(() => {
        el.querySelectorAll('.animate-on-scroll').forEach((item) => item.classList.add('is-visible'));
      }, 30);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="pt-20">

      {/* ── HERO ── */}
      <div
        ref={heroRef}
        className="relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #021A5E 0%, #0459F7 55%, #0A2FD8 100%)' }}
      >
        <div className="absolute inset-0 grid-lines opacity-15 pointer-events-none" />
        <div className="orb absolute w-[600px] h-[600px] rounded-full opacity-15 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 65%)', top: '-200px', right: '-150px' }} />
        <div className="orb-2 absolute w-[400px] h-[400px] rounded-full opacity-10 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(96,165,250,0.6) 0%, transparent 70%)', bottom: '-80px', left: '-80px' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left */}
            <div>
              <Link href="/products" className="animate-on-scroll stagger-1 inline-flex items-center gap-2 text-white/60 text-sm font-medium mb-7 hover:text-white transition-colors">
                <Icon name="ArrowLeftIcon" size={14} />
                Back to Products
              </Link>

              <span className="animate-on-scroll stagger-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/15 text-[11px] font-semibold tracking-widest text-white uppercase mb-5">
                <Icon name="PhoneIcon" size={11} />
                AI Call Center
              </span>

              <h1 className="animate-on-scroll stagger-2 font-display text-5xl md:text-6xl font-semibold text-white leading-tight tracking-tight mb-5 mt-2">
                AI Call Center —{' '}
                <span className="italic font-light" style={{ color: '#BFDBFE' }}>
                  Always On,
                </span>
                <br />Never Misses a Call.
              </h1>

              <p className="animate-on-scroll stagger-3 text-white/65 text-lg leading-relaxed mb-9 max-w-lg">
                Automate customer support, booking confirmations, and driver communications with intelligent AI voice bots — multilingual, available 24/7, and integrated with your CRM.
              </p>

              <div className="animate-on-scroll stagger-4 flex flex-wrap gap-4 mb-8">
                <Link href="/demo-booking" className="btn-primary inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-sm font-semibold">
                  Book a Free Demo
                  <Icon name="ArrowRightIcon" size={16} />
                </Link>
                <Link href="/contact" className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-sm font-medium text-white/75 border border-white/25 hover:border-white/50 hover:text-white transition-all">
                  <Icon name="PhoneIcon" size={15} />
                  +91 9600830488
                </Link>
              </div>

              <div className="animate-on-scroll stagger-5 flex flex-wrap gap-x-6 gap-y-2">
                {['24/7 availability', '5+ languages', 'CRM integrated', '80% auto-resolution'].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 text-xs text-white/50 font-medium">
                    <Icon name="CheckCircleIcon" size={13} className="text-[#BFDBFE]" />
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Right — live call simulation card */}
            <div className="animate-on-scroll stagger-3">
              <div
                className="rounded-2xl p-6 border border-white/15"
                style={{ background: 'rgba(255,255,255,0.07)', backdropFilter: 'blur(14px)' }}
              >
                <p className="text-[11px] font-semibold text-white/40 uppercase tracking-widest mb-5">Live Call Simulation</p>

                {/* Simulated call UI */}
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-white/[0.08] border border-white/10">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                      <Icon name="UserIcon" size={18} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-white/50 mb-0.5">Customer</p>
                      <p className="text-sm text-white font-medium">"Where is my delivery?"</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 px-4">
                    <div className="flex-1 h-px bg-white/10" />
                    <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
                      </span>
                      <span className="text-[10px] font-semibold text-white/60">AI Processing</span>
                    </div>
                    <div className="flex-1 h-px bg-white/10" />
                  </div>

                  <div className="flex items-start gap-3 p-4 rounded-xl border border-[#BFDBFE]/20" style={{ background: 'rgba(4,89,247,0.25)' }}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(4,89,247,0.6)' }}>
                      <Icon name="MicrophoneIcon" size={18} className="text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-white/50 mb-0.5">AI Voice Bot</p>
                      <p className="text-sm text-white font-medium">"Your order #1042 is out for delivery. Driver Rajan is 2.3 km away. ETA: 8 minutes."</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {[
                      { icon: 'ClockIcon', label: '1.8s Response' },
                      { icon: 'CheckCircleIcon', label: 'Resolved' },
                      { icon: 'DocumentTextIcon', label: 'Logged to CRM' },
                    ].map((s) => (
                      <div key={s.label} className="flex flex-col items-center gap-1.5 p-2.5 rounded-lg bg-white/[0.06] border border-white/10">
                        <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={14} className="text-[#BFDBFE]" />
                        <span className="text-[10px] font-semibold text-white/60 text-center leading-tight">{s.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── METRICS STRIP ── */}
      <div style={{ background: 'linear-gradient(135deg, #021A5E 0%, #0459F7 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {stats.map((s, i) => (
              <div key={s.label} className={`animate-on-scroll stagger-${i + 1} flex items-center gap-4 px-6 py-7`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-white/15">
                  <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-white" />
                </div>
                <div>
                  <div className="font-display text-2xl font-semibold text-white leading-none mb-0.5">{s.value}</div>
                  <div className="text-xs text-white/60 font-medium">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CORE FEATURES ── */}
      <div className="py-24 bg-[#EFF6FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(4,89,247,0.3)] bg-[rgba(4,89,247,0.08)] text-[11px] font-semibold tracking-widest text-[#0459F7] uppercase mb-4">
              Key Features
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Six capabilities that make your{' '}
              <span className="italic font-light text-[#0459F7]">support truly intelligent.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-2xl mx-auto">From inbound call handling to outbound confirmations — fully automated, multilingual, and always on.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {coreFeatures.map((f, i) => (
              <div
                key={f.num}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} group rounded-2xl p-7 border hover:shadow-lg transition-all duration-300 bg-white`}
                style={{ borderColor: i % 2 === 0 ? 'rgba(4,89,247,0.15)' : 'rgba(15,23,42,0.07)' }}
              >
                <div className="flex items-start gap-4 mb-4">
                  <span
                    className="font-mono-custom text-[11px] font-bold w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #0459F7, #0A2FD8)' }}
                  >
                    {f.num}
                  </span>
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                    style={{ background: 'rgba(4,89,247,0.1)' }}
                  >
                    <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#0459F7]" />
                  </div>
                  <h3 className="font-display text-base font-semibold text-[#0F172A] leading-snug pt-1 flex-1">{f.label}</h3>
                </div>
                <p className="text-sm text-[#475569] leading-relaxed mb-4">{f.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {f.pills.map((p) => (
                    <span key={p} className="text-[11px] font-semibold px-2.5 py-1 rounded-full text-[#0459F7]" style={{ background: 'rgba(4,89,247,0.08)' }}>
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── WORKFLOW ── */}
      <div className="py-24 relative overflow-hidden" style={{ background: '#021A5E' }}>
        <div className="absolute inset-0 grid-lines opacity-10 pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[300px] pointer-events-none"
          style={{ background: 'radial-gradient(ellipse, rgba(4,89,247,0.4) 0%, transparent 70%)', filter: 'blur(40px)' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#BFDBFE]/30 bg-[#BFDBFE]/10 text-[11px] font-semibold tracking-widest text-[#BFDBFE] uppercase mb-5">
              How It Works
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-white tracking-tight">
              From ring to resolution —{' '}
              <span className="italic font-light text-[#BFDBFE]">fully automated.</span>
            </h2>
          </div>

          <div className="relative grid sm:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="hidden lg:block absolute top-[26px] left-[calc(8.33%+20px)] right-[calc(8.33%+20px)] h-px"
              style={{ background: 'linear-gradient(90deg, rgba(191,219,254,0.05), rgba(191,219,254,0.3) 20%, rgba(191,219,254,0.3) 80%, rgba(191,219,254,0.05))' }} />

            {workflowSteps.map((step, i) => (
              <div key={step.title} className={`animate-on-scroll stagger-${i + 2} flex flex-col items-center text-center`}>
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 z-10"
                  style={{ background: 'linear-gradient(135deg, #0459F7, #0A2FD8)', boxShadow: '0 0 24px rgba(4,89,247,0.4)' }}
                >
                  <Icon name={step.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-white" />
                </div>
                <div className="text-[10px] font-bold text-[#BFDBFE]/50 tracking-widest uppercase mb-1.5">Step {i + 1}</div>
                <h3 className="font-semibold text-white text-sm mb-1.5">{step.title}</h3>
                <p className="text-xs text-white/45 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── USE CASES ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(4,89,247,0.3)] bg-[rgba(4,89,247,0.08)] text-[11px] font-semibold tracking-widest text-[#0459F7] uppercase mb-4">
              Use Cases
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Built for every{' '}
              <span className="italic font-light text-[#0459F7]">mobility vertical.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-xl mx-auto">Whether you run a taxi fleet, logistics operation, or e-commerce delivery — the AI call center adapts to your workflow.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {useCases.map((uc, i) => (
              <div
                key={uc.title}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} rounded-2xl overflow-hidden border border-[rgba(4,89,247,0.1)] hover:border-[rgba(4,89,247,0.3)] hover:shadow-md transition-all duration-300`}
              >
                <div className="p-5" style={{ background: 'linear-gradient(135deg, #0459F7, #0A2FD8)' }}>
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center mb-3">
                    <Icon name={uc.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-white" />
                  </div>
                  <h3 className="font-display text-base font-semibold text-white">{uc.title}</h3>
                </div>
                <div className="p-5 bg-[#EFF6FF] flex flex-col gap-2">
                  {uc.items.map((item) => (
                    <div key={item} className="flex items-center gap-2.5 p-2.5 rounded-lg bg-white border border-[rgba(4,89,247,0.08)]">
                      <div className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: 'rgba(4,89,247,0.12)' }}>
                        <Icon name="CheckIcon" size={10} className="text-[#0459F7]" />
                      </div>
                      <span className="text-xs font-medium text-[#0F172A]">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="py-24 bg-[#EFF6FF]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className="animate-on-scroll stagger-1 rounded-3xl p-12 md:p-16 relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #021A5E 0%, #0459F7 55%, #0A2FD8 100%)' }}
          >
            <div className="absolute inset-0 grid-lines opacity-15 pointer-events-none" />
            <div className="absolute top-0 right-0 w-72 h-72 pointer-events-none rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)', transform: 'translate(50px,-50px)' }} />

            <span className="relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/15 text-[11px] font-semibold tracking-widest text-white uppercase mb-6">
              <span className="relative flex h-1.5 w-1.5">
                <span className="ping-slow absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
              </span>
              Ready to Launch?
            </span>

            <h2 className="relative font-display text-4xl md:text-5xl font-semibold text-white tracking-tight mb-4">
              Let your AI handle the calls.{' '}
              <span className="italic font-light text-[#BFDBFE]">You focus on growth.</span>
            </h2>

            <p className="relative text-white/65 text-lg mb-5 max-w-xl mx-auto leading-relaxed">
              For demo or more information
            </p>

            <div className="relative flex flex-wrap justify-center gap-x-5 gap-y-2 mb-10 text-sm">
              <a href="tel:+919600830488" className="inline-flex items-center gap-2 text-white/70 font-medium hover:text-white transition-colors">
                <Icon name="PhoneIcon" size={14} />
                +91 9600830488
              </a>
              <span className="text-white/25 hidden sm:inline">|</span>
              <a href="mailto:contact@movio.in" className="inline-flex items-center gap-2 text-white/70 font-medium hover:text-white transition-colors">
                <Icon name="EnvelopeIcon" size={14} />
                contact@movio.in
              </a>
            </div>

            <div className="relative flex flex-wrap justify-center gap-4">
              <Link
                href="/demo-booking"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-semibold bg-white text-[#0459F7] hover:bg-white/90 shadow-lg transition-all duration-200 hover:-translate-y-0.5"
              >
                Book Free Demo
                <Icon name="ArrowRightIcon" size={18} />
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-medium text-white border-2 border-white/30 hover:border-white/65 hover:bg-white/10 transition-all duration-200"
              >
                <Icon name="PhoneIcon" size={16} />
                Talk to Sales
              </Link>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
